import Box from '@mui/material/Box'
import { Fragment, useEffect, useRef } from 'react'
import ObjectDeleteButton from './ObjectDeleteButton'
import ObjectSnapButton from './ObjectSnapButton'
import ObjectSeparateButton from './ObjectSeparateButton'
import { SceneManager } from '../../../visualization/sceneManager'
import { Matrix, Vector3 } from '@babylonjs/core'

const rawOptions = [
  {
    name: 'SnapObjects',
    component: () => ObjectSnapButton(),
  },
  {
    name: 'SeparateObjects',
    component: () => ObjectSeparateButton(),
  },
  {
    name: 'Delete',
    component: () => ObjectDeleteButton(),
  },
]

const ObjectOptions = () => {
  const ref = useRef<HTMLDivElement>(null)
  const options = rawOptions
    .map((o) => {
      const result = o.component()
      return result?.component ? { ...o, ...result } : null
    })
    .filter((o) => o !== null)

  useEffect(() => {
    const scene = SceneManager.getInstance().getScene()
    const engine = SceneManager.getInstance().getEngine()
    const camera = SceneManager.getInstance().getCamera()
    if (!scene || !camera || !engine || !options.length) return

    const observer = scene.onBeforeRenderObservable.add(() => {
      if (!ref.current) {
        return
      }

      const allMeshes = options.flatMap((o) => o?.meshes || [])
      if (!allMeshes.length) return

      let min = new Vector3(
        Number.POSITIVE_INFINITY,
        Number.POSITIVE_INFINITY,
        Number.POSITIVE_INFINITY,
      )
      let max = new Vector3(
        Number.NEGATIVE_INFINITY,
        Number.NEGATIVE_INFINITY,
        Number.NEGATIVE_INFINITY,
      )

      allMeshes.forEach((mesh) => {
        const { minimumWorld, maximumWorld } =
          mesh.getBoundingInfo().boundingBox
        min = Vector3.Minimize(min, minimumWorld)
        max = Vector3.Maximize(max, maximumWorld)
      })

      const centerY = min.y + (max.y - min.y) / 2
      const centerMax = new Vector3(max.x, centerY, max.z)
      const centerMin = new Vector3(min.x, centerY, min.z)

      const projectedCenterMax = Vector3.Project(
        centerMax,
        Matrix.Identity(),
        scene.getTransformMatrix(),
        camera.viewport.toGlobal(
          engine.getRenderWidth(true),
          engine.getRenderHeight(true),
        ),
      )
      const projectedCenterMin = Vector3.Project(
        centerMin,
        Matrix.Identity(),
        scene.getTransformMatrix(),
        camera.viewport.toGlobal(
          engine.getRenderWidth(true),
          engine.getRenderHeight(true),
        ),
      )
      const projected =
        projectedCenterMax.x > projectedCenterMin.x
          ? projectedCenterMax
          : projectedCenterMin

      const shiftByWidth = 12
      const buttonHeight = ref.current.offsetHeight
      ref.current.style.left = `${projected.x + shiftByWidth}px`
      ref.current.style.top = `${projected.y + buttonHeight / 2}px`
    })

    return () => {
      scene.onBeforeRenderObservable.remove(observer)
    }
  }, [options])

  if (!options.length) {
    return null
  }

  return (
    <Box
      ref={ref}
      gap='0.5rem'
      sx={{
        display: 'flex',
        background:
          'radial-gradient(50% 73.19% at 50% 50%, rgba(88, 88, 88, 0.3) 0%, rgba(204, 204, 204, 0.3) 100%)',
        borderRadius: '0.5rem',
        boxShadow: '0px 4px 4px 0px #00000040',
        p: '0.5rem',
        flexDirection: 'column',
        position: 'fixed',
        maxWidth: '10rem',
        height: 'auto',
        pointerEvents: 'all',
        zIndex: '20 !important',
      }}
    >
      {options.map((option) => (
        <Fragment key={option?.name}>{option?.component}</Fragment>
      ))}
    </Box>
  )
}

export default ObjectOptions
